import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      errorInfo,
    });

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-gradient-to-br from-danger-50 to-danger-100 dark:from-danger-900/10 dark:to-danger-800/10 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full bg-white dark:bg-bg-dark-tertiary rounded-lg shadow-lg p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-danger-100 dark:bg-danger-900/30 rounded-full flex items-center justify-center mr-4">
                <svg
                  className="w-6 h-6 text-danger-600 dark:text-danger-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-meradio-dark dark:text-meradio-cream">
                Something went wrong
              </h1>
            </div>

            <div className="mb-6">
              <p className="text-meradio-dark-light dark:text-meradio-dark-light mb-4">
                We're sorry, but something unexpected happened. This error has been logged and we'll look into it.
              </p>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mt-4 p-4 bg-neutral-50 dark:bg-neutral-900 rounded-lg">
                  <summary className="cursor-pointer font-medium text-sm text-neutral-700 dark:text-neutral-300 mb-2">
                    Error Details (Development Only)
                  </summary>
                  <div className="mt-2 text-xs font-mono text-danger-600 dark:text-danger-400">
                    <p className="font-bold mb-2">{this.state.error.toString()}</p>
                    {this.state.errorInfo && (
                      <pre className="whitespace-pre-wrap overflow-auto max-h-96 text-neutral-600 dark:text-neutral-400">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    )}
                  </div>
                </details>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button onClick={this.handleReset} className="flex-1" variant="primary">
                Try Again
              </Button>
              <Button
                onClick={() => window.location.reload()}
                className="flex-1"
                variant="outline"
              >
                Reload Page
              </Button>
              <Link
                to="/"
                className="flex-1 px-4 py-2 border border-neutral-300 dark:border-neutral-600 text-meradio-dark dark:text-meradio-cream hover:bg-neutral-50 dark:hover:bg-neutral-800 rounded-lg transition-colors font-medium text-center"
              >
                Go Home
              </Link>
            </div>

            <div className="mt-6 pt-6 border-t border-neutral-200 dark:border-neutral-700">
              <p className="text-sm text-meradio-dark-light dark:text-meradio-dark-light text-center">
                If this problem persists, please{' '}
                <a
                  href="mailto:support@meradio.com"
                  className="text-meradio-primary hover:text-meradio-primary-dark dark:hover:text-meradio-primary transition-colors underline"
                >
                  contact support
                </a>
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
